import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { CustomTranslateService } from '@services/common/custom-translate.service';
import { City } from 'app/@types/shared/world-cities';
import { OrganizationEntityDto } from 'app/@types/dto/organization-entity-dto.model';
import { combineLatest, Observable, startWith, Subject, switchMap } from 'rxjs';
import { IndustryDto } from 'app/@types/dto/industry-dto.model';
import { ConfirmationService } from 'primeng/api';
import { Store } from '@ngxs/store';
import { OrganizationAction } from '@state/organization.state';
import { OrganizationDto } from 'app/@types/dto/organization-dto.model';
import { QuestionnaireDto } from 'app/@types/dto/questionnaire-dto.model';
import { UtilsService } from '@services/common/utils.service';
import { QuestionnaireHttpService } from '@services/datasources/questionnaire-http.service';
import { CurrencyDto } from 'app/@types/dto/currency-dto.model';

interface Country {
    code: string;
    name: string;
}

interface Industry extends IndustryDto {
    label?: string;
}

export interface CurrencyOption {
    code: string;
    label: string;
}

@Component({
    selector: 'app-edit-entity',
    templateUrl: './edit-entity.component.html',
    styleUrls: ['./edit-entity.component.scss'],
    providers: [ConfirmationService],
})
export class EditEntityComponent {
    @Input() public organizationId: string;
    @Input() public entity: OrganizationEntityDto | null = null;
    @Input() public cities: Map<string, City[]>;
    @Input() public industries: Industry[];
    @Input() public activeQuestionnaire: QuestionnaireDto;
    @Input() public entities: OrganizationEntityDto[];
    @Input() public currencies: CurrencyDto[] = [];
    @Output() closeEditEntity = new EventEmitter<
        { translateKey: string; status: boolean; hasFteDecreased?: boolean } | undefined
    >();
    public entityForm: FormGroup;
    public filteredCities: City[] | undefined;
    public countries: Country[] = [];
    public undeletedEntities: OrganizationEntityDto[];
    public currencyOptions: CurrencyOption[] = [];

    constructor(
        private formBuilder: FormBuilder,
        private translateService: CustomTranslateService,
        private ts: TranslateService,
        private confirmationService: ConfirmationService,
        private store: Store,
        private readonly utilsService: UtilsService,
    ) {
        this.entityForm = this.formBuilder.group({
            name: new FormControl('', Validators.required),
            country: new FormControl('', Validators.required),
            city: new FormControl('0000000'),
            industry: new FormControl(''),
            fte: new FormControl(1, [Validators.required, Validators.min(1)]),
            growth: new FormControl(0, Validators.min(0)),
            revenues: new FormControl(undefined, Validators.min(0)),
            currency: new FormControl('EUR', Validators.required),
        });
    }

    ngOnInit() {
        this.translateService.onLangChange.pipe(startWith(null)).subscribe((lang) => {
            this.countries = [];
            this.translateService.get('countries').subscribe((countries) => {
                Object.entries(countries).forEach(([code, name]) => {
                    this.countries.push({ code: code, name: name });
                });
                this.countries.sort((a, b) => a.name.localeCompare(b.name));
            });

            this.industries.forEach((industry) => {
                industry.label = this.translateService.instant(
                    `components.settingsForm.industry.${industry.industrySectorLabel}`,
                );
            });
            this.industries.sort((a, b) => a.label!.localeCompare(b.label!));

            this.currencyOptions = this.currencies.map((currency) => {
                return {
                    code: currency.code,
                    label: this.ts.instant(`common.currency.${currency.code}`),
                };
            });
        });

        if (this.entity !== null) {
            this.entityForm.get('name')?.setValue(this.entity.name);
            this.entityForm.get('fte')?.setValue(this.entity.fulltimeEqCount);
            this.entityForm.get('country')?.setValue(this.entity.country);
            this.filteredCities = this.cities.get(this.entity.country!);
            this.entityForm.get('city')?.setValue(parseInt(this.entity.cityCode ?? '0000000'));
            this.entityForm.get('industry')?.setValue(this.entity.industryCode);
            this.entityForm.get('revenues')?.setValue(this.entity.annualRevenue);
            this.entityForm.get('currency')?.setValue(this.entity.currency);
        } else {
            this.entityForm.get('city')?.disable();
        }
        this.undeletedEntities = this.entities.filter((e) => !e.deleted);
    }

    filterCities() {
        const country = this.entityForm.get('country')?.value;
        if (country) {
            this.filteredCities = this.cities.get(country);
            this.entityForm.get('city')?.enable();
        }
    }

    public getIndustryDisplay(industryId: string): string {
        const res = this.industries.find((x) => x.industrySectorId === industryId);

        return res
            ? this.translateService.instant(`components.settingsForm.industry.${res.industrySectorLabel}`)
            : 'N/A';
    }

    save(): void {
        const entity: OrganizationEntityDto = {
            organizationId: this.organizationId,
            name: this.entityForm.get('name')!.value,
            industryCode: this.entityForm.get('industry')!.value,
            country: this.entityForm.get('country')!.value,
            cityCode: this.entityForm.get('city')!.value,
            annualRevenue: this.entityForm.get('revenues')!.value,
            fulltimeEqCount: this.entityForm.get('fte')!.value,
            isActive: true, //To remove??
            deleted: false,
            currency: this.entityForm.get('currency')!.value,
        };
        if (this.entity === null) {
            this.createEntity(entity);
        } else {
            this.updateEntity(entity);
        }
    }

    createEntity(entity: OrganizationEntityDto) {
        if (this.entityForm.valid) {
            let callCompletion: Subject<OrganizationEntityDto | undefined> = new Subject();
            this.store.dispatch(
                new OrganizationAction.CreateEntitiesAction(this.organizationId, entity, callCompletion),
            );
            callCompletion.subscribe({
                next: () => {
                    this.closeEditEntity.emit({
                        translateKey: 'components.settingsForm.entitySection.toasts.creationSuccess',
                        status: true,
                    });
                },
                error: () => {
                    this.closeEditEntity.emit({
                        translateKey: 'components.settingsForm.entitySection.toasts.creationFail',
                        status: false,
                    });
                },
            });
        }
    }

    updateEntity(entity: OrganizationEntityDto) {
        if (this.entityForm.valid) {
            let callCompletion: Subject<OrganizationEntityDto | undefined> = new Subject();
            if (this.entity?.id) {
                entity.id = this.entity.id;
                this.store.dispatch(
                    new OrganizationAction.UpdateEntitieAction(
                        this.organizationId,
                        this.entity?.id,
                        entity,
                        callCompletion,
                    ),
                );
                let hasFteDecreased: boolean = false;
                if (this.entity?.fulltimeEqCount && entity.fulltimeEqCount) {
                    hasFteDecreased = this.entity.fulltimeEqCount > entity.fulltimeEqCount;
                }
                callCompletion.subscribe({
                    next: () => {
                        this.closeEditEntity.emit({
                            translateKey: 'components.settingsForm.entitySection.toasts.updateSuccess',
                            hasFteDecreased: hasFteDecreased,
                            status: true,
                        });
                    },
                    error: () => {
                        this.closeEditEntity.emit({
                            translateKey: 'components.settingsForm.entitySection.toasts.updateFail',
                            status: false,
                        });
                    },
                });
            }
        }
    }

    close() {
        this.closeEditEntity.emit();
    }

    onDelete(): void {
        combineLatest([
            this.translateService.get('common.confirm_message'),
            this.translateService.get('common.confirm_title'),
            this.translateService.get('common.confirm_button'),
            this.translateService.get('common.cancel_button'),
        ]).subscribe((translations) => {
            this.confirmationService.confirm({
                message: this.utilsService.handleTranslation(translations[0]),
                header: this.utilsService.handleTranslation(translations[1]),
                acceptLabel: this.utilsService.handleTranslation(translations[2]),
                rejectLabel: this.utilsService.handleTranslation(translations[3]),
                icon: 'pi pi-exclamation-triangle',
                accept: () => {
                    if (this.entity) {
                        const callCompletion = new Subject<QuestionnaireDto[] | undefined>();
                        this.store.dispatch(
                            new OrganizationAction.DeleteEntityAction(
                                this.organizationId,
                                this.entity.id!,
                                this.activeQuestionnaire,
                                callCompletion,
                            ),
                        );
                        callCompletion.subscribe({
                            next: () => {
                                this.closeEditEntity.emit({
                                    translateKey: 'components.settingsForm.entitySection.toasts.deleteSuccess',
                                    status: true,
                                });
                            },
                            error: () => {
                                this.closeEditEntity.emit({
                                    translateKey: 'components.settingsForm.entitySection.toasts.deleteFail',
                                    status: true,
                                });
                            },
                        });
                    }
                },
                reject: (type) => {},
            });
        });
    }
    onLabelClick(id: string) {
        const primeInput = document.getElementById(id);
        if (primeInput) {
            const input = this.getInputElement(primeInput);
            input?.focus();
        }
    }
    getInputElement(primeInput: HTMLElement): HTMLElement | null {
        return (
            primeInput.querySelector('p-dropdown') ||
            primeInput.querySelector('p-radiobutton') ||
            primeInput.querySelector('input')
        );
    }
}
